import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import clsx from 'clsx'
import OfferCardCarouselSection, {
  type OfferCardCarouselSectionFilterChangeMeta,
  OFFER_CARD_CAROUSEL_SECTION_NESTED_CLASS,
} from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import type { TrendingDestination } from '../TrendingDestinations/constants'
import {
  useLocalDestinationOfferCarousel,
  type LocalDestinationOfferCarouselFilterOption,
  type TrendingDestinationWithId,
} from 'home/hooks/useLocalDestinationOfferCarousel'
import OfferListEventsContext, {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import { CAROUSEL_IN_VIEW_PARAMS } from 'home/constants'
import { LOCAL_DESTINATION_OFFER_CAROUSEL_FILTERS } from './localDestinationOfferCarouselConstants'
import {
  buildLocalDestinationOfferCarouselAnalyticsData,
  LOCAL_DESTINATION_OFFER_CAROUSEL_CHIP_CHANGE_SUBJECT,
  LOCAL_DESTINATION_OFFER_CAROUSEL_MODULE_ID,
  resolvePendingLocalDestinationChipAnalyticsDestinationId,
  shouldDispatchLocalDestinationChipChangeAnalytics,
} from './localDestinationOfferCarouselAnalytics'
import { shouldRenderLocalDestinationOfferCarouselBody } from './localDestinationOfferCarouselModel'
import { buildLocalDestinationViewAllUrl } from './localDestinationOfferCarouselUtils'
import LocalDestinationOfferCarouselSkeleton from './LocalDestinationOfferCarouselSkeleton'

interface Props {
  trendingDestinations: Array<TrendingDestination>
  regionCode: string
  onListEvent: OfferListEventHandler
}

interface CarouselBodyProps {
  destinations: Array<TrendingDestinationWithId>
  destinationFilterOptions: Array<LocalDestinationOfferCarouselFilterOption>
  defaultDestinationId: string
  selectedFilter: string
  onSelectedFilterChange: (destinationId?: string) => void
}

function LocalDestinationOfferCarouselBody(props: CarouselBodyProps) {
  const {
    destinations,
    destinationFilterOptions,
    defaultDestinationId,
    selectedFilter,
    onSelectedFilterChange,
  } = props
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const pendingUserChipAnalyticsDestinationIdRef = useRef<string | undefined>(undefined)

  const buildAllLink = useCallback((selectedFilter?: string) => {
    return buildLocalDestinationViewAllUrl(destinations, selectedFilter)
  }, [destinations])

  const handleSelectedFilterChange = useCallback((
    value?: string,
    meta?: OfferCardCarouselSectionFilterChangeMeta,
  ) => {
    pendingUserChipAnalyticsDestinationIdRef.current = resolvePendingLocalDestinationChipAnalyticsDestinationId(
      value,
      selectedFilter,
      meta?.source,
    )
    onSelectedFilterChange(value)
  }, [onSelectedFilterChange, selectedFilter])

  useEffect(() => {
    const pendingDestinationId = pendingUserChipAnalyticsDestinationIdRef.current
    if (!shouldDispatchLocalDestinationChipChangeAnalytics(selectedFilter, pendingDestinationId)) {
      return
    }

    pendingUserChipAnalyticsDestinationIdRef.current = undefined

    dispatchOfferListEvent({
      type: OfferListEvents.clientAction,
      subject: LOCAL_DESTINATION_OFFER_CAROUSEL_CHIP_CHANGE_SUBJECT,
      action: 'selected',
      key: EventDataKey.ClientAction,
    })
  }, [dispatchOfferListEvent, selectedFilter])

  const title = useMemo(() => (
    <FormattedMessage
      id="03VRZhvztxvu87nCYtle"
      defaultMessage="Popular <i>hotel</i> offers in trending destinations"
      description="Heading for the homepage hotel offer carousel shown below trending destinations in international markets"
      values={{
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      }}
    />
  ), [])

  return (
    <OfferCardCarouselSection
      filters={LOCAL_DESTINATION_OFFER_CAROUSEL_FILTERS}
      title={title}
      additionalFilters="destinations"
      additionalFilterOptions={destinationFilterOptions}
      maxFilterOptions={destinationFilterOptions.length}
      selectedFilter={selectedFilter}
      defaultFilter={defaultDestinationId}
      buildAllLink={buildAllLink}
      onSelectedFilterChange={handleSelectedFilterChange}
      resetFilterWhenSelectionInvalid
      className={clsx(OFFER_CARD_CAROUSEL_SECTION_NESTED_CLASS)}
      tileStyle="card-default"
      tileSize="medium"
      data-testid={LOCAL_DESTINATION_OFFER_CAROUSEL_MODULE_ID}
    />
  )
}

function LocalDestinationOfferCarousel(props: Props) {
  const { trendingDestinations, regionCode, onListEvent } = props
  const [inViewRef, inView] = useInView(CAROUSEL_IN_VIEW_PARAMS)
  const {
    trendingDestinationsWithIds,
    destinations,
    destinationFilterOptions,
    defaultDestinationId,
    checkingEligibility,
    allChecksComplete,
  } = useLocalDestinationOfferCarousel(trendingDestinations, { enabled: inView })
  const [selectedDestinationId, setSelectedDestinationId] = useState<string | undefined>()

  useEffect(() => {
    if (!inView) {
      return
    }

    setSelectedDestinationId((current) => {
      if (current === undefined) {
        return current
      }

      if (destinations.some(destination => destination.destinationId === current)) {
        return current
      }

      return undefined
    })
  }, [destinations, inView])

  const handleSelectedDestinationChange = useCallback((destinationId?: string) => {
    setSelectedDestinationId(destinationId)
  }, [])

  const activeDestinationId = (
    selectedDestinationId
    && destinations.some(destination => destination.destinationId === selectedDestinationId)
      ? selectedDestinationId
      : undefined
  ) ?? defaultDestinationId

  const analyticsAdditionalData = useMemo(() => {
    return buildLocalDestinationOfferCarouselAnalyticsData({
      marketCode: regionCode,
      trendingDestinations: trendingDestinationsWithIds,
      eligibleDestinations: destinations,
      selectedDestinationId: activeDestinationId,
      defaultDestinationId,
    })
  }, [
    activeDestinationId,
    defaultDestinationId,
    destinations,
    regionCode,
    trendingDestinationsWithIds,
  ])

  const shouldRenderCarouselBody = shouldRenderLocalDestinationOfferCarouselBody({
    defaultDestinationId,
    allChecksComplete,
    eligibleDestinationCount: destinations.length,
  })

  let content: React.ReactNode = null

  if (!inView) {
    // Sentinel only: keep eligibility fetches off the critical path until near viewport.
    content = null
  } else if (checkingEligibility) {
    content = <LocalDestinationOfferCarouselSkeleton />
  } else if (shouldRenderCarouselBody && defaultDestinationId) {
    content = (
      <OfferListEventsProvider onListEvent={onListEvent} additionalData={analyticsAdditionalData}>
        <LocalDestinationOfferCarouselBody
          destinations={destinations}
          destinationFilterOptions={destinationFilterOptions}
          defaultDestinationId={defaultDestinationId}
          selectedFilter={activeDestinationId ?? defaultDestinationId}
          onSelectedFilterChange={handleSelectedDestinationChange}
        />
      </OfferListEventsProvider>
    )
  }

  return (
    <div ref={inViewRef} aria-busy={inView && checkingEligibility}>
      {content}
    </div>
  )
}

export default React.memo(LocalDestinationOfferCarousel)
